/**
 * The first rows of an uploaded outputs file, each marked as matching an item
 * of the chosen dataset version or not. Unknown keys are flagged here so they
 * can be fixed before import; the server rejects them too.
 */

import { Pill } from "@/components/ui/pill";
import { plural, preview } from "@/lib/format";
import type { ParsedRecord } from "./recorded-file";

const SHOWN = 20;

export function RecordedPreview({
  fileName,
  records,
  known,
}: {
  fileName: string;
  records: ParsedRecord[];
  /** Null while the dataset's items are loading. */
  known: Set<string> | null;
}) {
  const unknown = known ? records.filter((r) => !known.has(r.ref)) : [];
  return (
    <section aria-label="Outputs preview" className="flex flex-col gap-2">
      <p className="text-xs text-muted">
        {fileName}: {plural(records.length, "record")}
        {known &&
          (unknown.length ? (
            <span role="alert" className="text-err">
              {" "}
              · {plural(unknown.length, "key")} not in this dataset version:{" "}
              {[...new Set(unknown.map((r) => r.ref))].slice(0, 5).join(", ")}
            </span>
          ) : (
            " · every key matches an item"
          ))}
      </p>
      <div className="overflow-x-auto rounded-[9px] border border-line">
        <table className="tbl text-xs">
          <thead>
            <tr>
              <th>Item</th>
              <th className="num">Rep.</th>
              <th>Output</th>
              <th>Match</th>
            </tr>
          </thead>
          <tbody>
            {records.slice(0, SHOWN).map((r, i) => {
              const ok = !known || known.has(r.ref);
              return (
                // Records have no id of their own and the file is never re-ordered, so the row number is stable.
                <tr key={`${r.ref}-${r.record.repetition ?? 1}-${i}`}>
                  <td className="font-mono">{r.ref}</td>
                  <td className="num">{r.record.repetition ?? 1}</td>
                  <td className="max-w-[260px]">
                    <span className="clamp-2">
                      {r.record.error ? `Error: ${r.record.error}` : preview(r.record.output, 120)}
                    </span>
                  </td>
                  <td>
                    {known ? (
                      <Pill tone={ok ? "neutral" : "err"}>{ok ? "Matched" : "Unknown key"}</Pill>
                    ) : (
                      "…"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {records.length > SHOWN && <span className="text-xs text-muted">Showing the first {SHOWN}.</span>}
    </section>
  );
}
